import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, clearToken, getToken } from "../lib/api";
import { BrandMark } from "../components/BrandMark";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Search, LogOut, FileText, Inbox } from "lucide-react";

const formatDate = (iso) =>
  new Date(iso).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

export default function AdminDashboard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin/login");
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/admin/submissions");
        setItems(data);
      } catch {
        clearToken();
        navigate("/admin/login");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const filtered = useMemo(() => {
    if (!query.trim()) return items;
    const q = query.toLowerCase();
    return items.filter(
      (i) =>
        (i.seller_name || "").toLowerCase().includes(q) ||
        (i.property_address || "").toLowerCase().includes(q) ||
        (i.email || "").toLowerCase().includes(q)
    );
  }, [items, query]);

  const logout = () => {
    clearToken();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8] flex" data-testid="admin-dashboard-page">
      {/* Sidebar */}
      <aside className="w-64 bg-[#010082] text-white flex flex-col p-6 lg:sticky lg:top-0 lg:h-screen">
        <BrandMark
          withWordmark={true}
          wordmarkClassName="[&>div:first-child]:text-white [&>div:last-child]:text-white/60"
        />
        <nav className="mt-12 space-y-1 text-[13px]">
          <div className="px-3 py-2 rounded-md bg-white/10 flex items-center gap-2">
            <Inbox className="w-4 h-4" strokeWidth={1.5} />
            Submissions
          </div>
        </nav>
        <div className="mt-auto">
          <button
            onClick={logout}
            className="text-[12px] uppercase tracking-[0.2em] text-white/70 hover:text-white flex items-center gap-2"
            data-testid="admin-logout-button"
          >
            <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} />
            Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 p-8 lg:p-12">
        <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#010082]">
              Admin
            </div>
            <h1 className="font-serif-display text-3xl sm:text-4xl text-[#1A1A1A] mt-1 tracking-tight">
              Seller Submissions
            </h1>
            <p className="text-[13px] text-[#737373] mt-2">
              {loading ? "Loading…" : `${items.length} total submissions`}
            </p>
          </div>
          <div className="relative w-full sm:w-80">
            <Search className="w-4 h-4 text-[#737373] absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, address, email…"
              className="h-11 pl-9 bg-white border border-[#E5E5E0]"
              data-testid="submissions-search-input"
            />
          </div>
        </div>

        <div className="bg-white border border-[#E5E5E0] rounded-xl overflow-hidden">
          <Table data-testid="submissions-table">
            <TableHeader className="bg-[#F3F2EE]">
              <TableRow>
                <TableHead className="text-[#4A4A4A]">Seller</TableHead>
                <TableHead className="text-[#4A4A4A]">Property</TableHead>
                <TableHead className="text-[#4A4A4A]">Email</TableHead>
                <TableHead className="text-[#4A4A4A]">Submitted</TableHead>
                <TableHead className="text-right text-[#4A4A4A]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-16 text-[#737373]">
                    <div className="flex flex-col items-center gap-3">
                      <Inbox className="w-8 h-8 text-[#c9c8c3]" strokeWidth={1.25} />
                      <div>No submissions yet.</div>
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((s) => (
                <TableRow
                  key={s.id}
                  className="hover:bg-[#FAFAF8] cursor-pointer"
                  data-testid={`submission-row-${s.id}`}
                >
                  <TableCell className="font-medium text-[#1A1A1A]">
                    {s.seller_name || "—"}
                  </TableCell>
                  <TableCell className="text-[#4A4A4A]">
                    {s.property_address || "—"}
                  </TableCell>
                  <TableCell className="text-[#4A4A4A]">{s.email || "—"}</TableCell>
                  <TableCell className="text-[#737373] text-[13px]">
                    {formatDate(s.submitted_at)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Link to={`/admin/submissions/${s.id}`}>
                      <Button
                        variant="outline"
                        className="h-9 border-[#010082]/30 text-[#010082] hover:bg-[#010082]/[0.04] bg-transparent"
                        data-testid={`view-submission-${s.id}`}
                      >
                        <FileText className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.75} />
                        View
                      </Button>
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </main>
    </div>
  );
}
